// The burst. A big win throws confetti up from the bottom in the market's colours; a bust flashes
// red at the edges and drops dark glass. One canvas over the page, drawn only while something
// moves, its bitmap let go when still. With reduced motion: one short flash, nothing flying.

import { useEffect, useRef } from "react";
import "./fx.css";

export type BurstTone = "up" | "down";

type Rgb = readonly [number, number, number];

type Bit = {
  shape: "strip" | "dot" | "shard";
  /** Centre and velocity, css px and px/s. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Gravity, px/s², and air drag, 1/s. */
  g: number;
  drag: number;
  angle: number;
  spin: number;
  /** The tumble: a strip or a shard turning edge-on and back. */
  flip: number;
  flipRate: number;
  size: number;
  fill: string;
  /** One bright edge, for glass. */
  edge: string | null;
  /** A shard's outline around its centre, x and y in turn. */
  outline: number[];
  /** Seconds: waits `delay`, then lives `life`. */
  delay: number;
  life: number;
  age: number;
};

type Flash = { rgb: Rgb; peak: number; rise: number; fall: number; age: number };

type Engine = { fire(tone: BurstTone): void; stop(): void };

const TAU = Math.PI * 2;
const STRIPS_AND_DOTS = 80;
const SHARDS = 40;

/** Plays one burst each time `fire` changes to a new non-zero value. Keep it mounted. */
export function Burst({ fire, tone }: { fire: number; tone: BurstTone }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const engine = useRef<Engine | null>(null);
  // the value at mount is history, not news
  const seen = useRef(fire);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const made = createEngine(el);
    engine.current = made;
    return () => {
      made.stop();
      engine.current = null;
    };
  }, []);

  useEffect(() => {
    if (fire === 0 || fire === seen.current) return;
    seen.current = fire;
    engine.current?.fire(tone);
  }, [fire, tone]);

  return <canvas ref={canvas} className="fx-burst" aria-hidden />;
}

// ---------- colour ----------

const rgba = ([r, g, b]: Rgb, a: number) => `rgba(${r}, ${g}, ${b}, ${a})`;

/** A token as rgb, through the canvas so any colour the browser knows reads; null when unset. */
function readRgb(ctx: CanvasRenderingContext2D, css: CSSStyleDeclaration, name: string): Rgb | null {
  const value = css.getPropertyValue(name).trim();
  if (!value) return null;
  ctx.fillStyle = "transparent";
  const blank = ctx.fillStyle;
  ctx.fillStyle = value;
  const resolved = String(ctx.fillStyle);
  if (resolved === blank) return null;
  const hex = /^#([0-9a-f]{6})$/i.exec(resolved);
  if (hex?.[1]) {
    const n = parseInt(hex[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(resolved);
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
}

const scaled = ([r, g, b]: Rgb, k: number): Rgb => [Math.round(r * k), Math.round(g * k), Math.round(b * k)];

// ---------- the engine ----------

const random = (from: number, to: number) => from + Math.random() * (to - from);

/** A shard: three or four corners at uneven radii, stretched a little, like broken glass. */
function shardOutline(size: number): number[] {
  const corners = Math.random() < 0.5 ? 3 : 4;
  const turn = Math.random() * TAU;
  const out: number[] = [];
  for (let i = 0; i < corners; i++) {
    const a = turn + (i / corners) * TAU + random(-0.35, 0.35);
    const r = size * random(0.45, 1);
    out.push(Math.cos(a) * r * 1.5, Math.sin(a) * r);
  }
  return out;
}

function createEngine(canvas: HTMLCanvasElement): Engine {
  const ctx = canvas.getContext("2d");
  let bits: Bit[] = [];
  let flashes: Flash[] = [];
  let frame = 0;
  let last = 0;
  // size, css px
  let width = 0;
  let height = 0;
  let dpr = 1;

  function fit() {
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    const d = Math.min(2, window.devicePixelRatio || 1);
    if (w === width && h === height && d === dpr) return;
    width = w;
    height = h;
    dpr = d;
    canvas.width = Math.max(1, Math.round(w * d));
    canvas.height = Math.max(1, Math.round(h * d));
  }

  // still: the bitmap goes, so the idle overlay costs nothing
  function release() {
    canvas.width = 0;
    canvas.height = 0;
    width = 0;
    height = 0;
  }
  release();

  function confetti(up: Rgb, accent: Rgb) {
    const unit = Math.min(1.35, Math.max(0.75, Math.min(width, height) / 600));
    // narrow on a phone, wider on a desk
    const spread = 0.14 + 0.32 * Math.min(1, width / height);
    const upFill = rgba(up, 1);
    const accentFill = rgba(accent, 1);
    for (let i = 0; i < STRIPS_AND_DOTS; i++) {
      const strip = i % 3 !== 0;
      const angle = -Math.PI / 2 + random(-spread, spread);
      // in viewport heights a second: strips climb a half to most of the screen, then flutter
      const speed = height * (strip ? random(2, 2.9) : random(1.5, 2.1));
      bits.push({
        shape: strip ? "strip" : "dot",
        x: width / 2 + random(-1, 1) * Math.min(48, width * 0.06),
        y: height + 12,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        g: height * 1.6,
        drag: strip ? 1.8 : 0.9,
        angle: Math.random() * TAU,
        spin: random(-9, 9),
        flip: Math.random() * TAU,
        flipRate: strip ? random(6, 14) : 0,
        size: (strip ? random(9, 13) : random(5, 8)) * unit,
        fill: Math.random() < 0.6 ? upFill : accentFill,
        edge: null,
        outline: [],
        // two volleys, the second a beat behind
        delay: i < 50 ? random(0, 0.04) : random(0.08, 0.14),
        life: random(1.15, 1.45),
        age: 0,
      });
    }
  }

  function glass(down: Rgb) {
    const unit = Math.min(1.35, Math.max(0.75, Math.min(width, height) / 600));
    const fill = rgba(scaled(down, 0.42), 0.92);
    const edge = rgba(down, 0.85);
    flashes.push({ rgb: down, peak: 1, rise: 0.04, fall: 0.26, age: 0 });
    for (let i = 0; i < SHARDS; i++) {
      const across = random(-1, 1);
      const size = random(6, 16) * unit;
      bits.push({
        shape: "shard",
        x: width / 2 + across * width * 0.32,
        y: height * random(0.22, 0.38),
        // a small kick out and up, then they fall
        vx: across * height * random(0.08, 0.26),
        vy: -height * random(0.04, 0.26),
        g: height * 2.4,
        drag: 0.3,
        angle: Math.random() * TAU,
        spin: random(-5, 5),
        flip: Math.random() * TAU,
        flipRate: random(3, 8),
        size,
        fill,
        edge,
        outline: shardOutline(size),
        delay: random(0, 0.05),
        life: random(0.9, 1.25),
        age: 0,
      });
    }
  }

  function drawFlash(flash: Flash) {
    if (!ctx) return;
    const t = flash.age;
    const level = t < flash.rise ? t / flash.rise : (1 - Math.min(1, (t - flash.rise) / flash.fall)) ** 2;
    const cx = width / 2;
    const cy = height / 2;
    const outer = Math.hypot(cx, cy);
    const vignette = ctx.createRadialGradient(cx, cy, outer * 0.3, cx, cy, outer);
    vignette.addColorStop(0, rgba(flash.rgb, 0));
    vignette.addColorStop(0.6, rgba(flash.rgb, 0.14));
    vignette.addColorStop(1, rgba(flash.rgb, 0.5));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = Math.max(0, Math.min(1, level * flash.peak));
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, width, height);
  }

  function drawBit(bit: Bit) {
    if (!ctx) return;
    const t = bit.age - bit.delay;
    if (t < 0) return;
    // whole for the first half of its life, then gone smoothly
    const x = Math.min(1, Math.max(0, (t / bit.life - 0.55) / 0.45));
    const tumble = Math.cos(bit.flip);
    // the back of a strip catches less light
    ctx.globalAlpha = (1 - x * x * (3 - 2 * x)) * (tumble < 0 ? 0.75 : 1);
    const cos = Math.cos(bit.angle) * dpr;
    const sin = Math.sin(bit.angle) * dpr;
    const k = bit.shape === "dot" ? 1 : tumble;
    ctx.setTransform(cos, sin, -sin * k, cos * k, bit.x * dpr, bit.y * dpr);
    ctx.fillStyle = bit.fill;
    if (bit.shape === "strip") {
      ctx.fillRect(-bit.size * 0.22, -bit.size / 2, bit.size * 0.44, bit.size);
      return;
    }
    ctx.beginPath();
    if (bit.shape === "dot") {
      ctx.arc(0, 0, bit.size * 0.28, 0, TAU);
      ctx.fill();
      return;
    }
    const outline = bit.outline;
    for (let i = 0; i + 1 < outline.length; i += 2) {
      const px = outline[i] ?? 0;
      const py = outline[i + 1] ?? 0;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
    if (bit.edge) {
      ctx.beginPath();
      ctx.moveTo(outline[0] ?? 0, outline[1] ?? 0);
      ctx.lineTo(outline[2] ?? 0, outline[3] ?? 0);
      ctx.strokeStyle = bit.edge;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  function advance(bit: Bit, dt: number): boolean {
    bit.age += dt;
    const t = bit.age - bit.delay;
    if (t < 0) return true;
    if (t > bit.life) return false;
    bit.vy += bit.g * dt;
    const keep = Math.exp(-bit.drag * dt);
    bit.vx *= keep;
    bit.vy *= keep;
    bit.x += bit.vx * dt;
    bit.y += bit.vy * dt;
    bit.angle += bit.spin * dt;
    bit.flip += bit.flipRate * dt;
    // fallen off the bottom: done early
    return !(bit.vy > 0 && bit.y > height + 40);
  }

  function step(now: number) {
    if (!ctx) return;
    // a background tab resumes where it was, not with a jump
    const dt = Math.min(1 / 30, Math.max(0, (now - last) / 1000));
    last = now;
    fit();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const flash of flashes) flash.age += dt;
    flashes = flashes.filter((flash) => flash.age < flash.rise + flash.fall);
    bits = bits.filter((bit) => advance(bit, dt));
    flashes.forEach(drawFlash);
    bits.forEach(drawBit);
    if (bits.length || flashes.length) {
      frame = requestAnimationFrame(step);
      return;
    }
    frame = 0;
    release();
  }

  return {
    fire(tone) {
      if (!ctx) return;
      fit();
      if (!width || !height) return;
      const css = getComputedStyle(document.documentElement);
      const up = readRgb(ctx, css, "--c-up");
      const accent = readRgb(ctx, css, "--c-accent") ?? up;
      const down = readRgb(ctx, css, "--c-down");
      const calm = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      if (tone === "up") {
        if (!up || !accent) return;
        if (calm) flashes.push({ rgb: up, peak: 0.6, rise: 0.02, fall: 0.13, age: 0 });
        else confetti(up, accent);
      } else {
        if (!down) return;
        if (calm) flashes.push({ rgb: down, peak: 0.6, rise: 0.02, fall: 0.13, age: 0 });
        else glass(down);
      }
      if (!frame) {
        last = performance.now();
        frame = requestAnimationFrame(step);
      }
    },
    stop() {
      cancelAnimationFrame(frame);
      frame = 0;
      bits = [];
      flashes = [];
      release();
    },
  };
}
